'use client'

import { PortableTextBlock } from "next-sanity";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Image } from "next-sanity/image";

import CustomPortableText from "@/components/portable-text";
import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import MuxPlayer from "@mux/mux-player-react";
import { urlForImage } from "@/sanity/lib/utils";

import useMediaQuery from "@/hooks/useMediaQuery";

type FullBleedMediaProps = Extract<NonNullable<NonNullable<HomepageQueryResult>["content"]>[number], { _type: "fullBleedMedia" }> | Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "fullBleedMedia" }>;

export default function FullBleedMedia(props: FullBleedMediaProps) {
  const {content, alignment, backgroundType, video, mobileVideo, image, mobileImage} = props;

  const [media, setMedia] = useState(video || image);

  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    if (isDesktop) {
      setMedia(video || image);
    } else {
      setMedia(mobileVideo || mobileImage || video || image);
    }
  }, [isDesktop, video, mobileVideo, image, mobileImage]);


  return (
    <section className="full-bleed-media grid relative min-h-svh">
      <div className="media col-start-1 col-span-1 row-start-1 row-span-1">
        {backgroundType === 'video' && media?._type === 'mux.video' && media?.asset?.playbackId && (
          <MuxPlayer
            style={{ aspectRatio: '16/9' }}
            streamType="on-demand"
            playbackId={media.asset.playbackId}
            metadata={{
              video_id: media.asset?.assetId || undefined,
              video_title: (content?.[0] as any)?.children?.[0]?.text || 'Full Bleed Media',
            }}
            autoPlay
            loop
            muted
            playsInline
          />
        )}

        {backgroundType === 'image' && media?._type === 'imageAlt' && (() => {
          const m = media as any;
          const imageSource = m.assetRef
            ? { _type: 'image', asset: { _ref: m.assetRef, _type: 'reference' }, hotspot: m.hotspot, crop: m.crop }
            : null;
          const src = imageSource ? urlForImage(imageSource)?.fit('crop').crop('focalpoint').url() : null;
          if (!src) return null;
          const hotspot = m.hotspot;
          const objectPosition = hotspot
            ? `${Math.round(hotspot.x * 100)}% ${Math.round(hotspot.y * 100)}%`
            : 'center';
          return (
            <Image
              alt={(media as any).alt || 'Full Bleed Media'}
              className="object-cover"
              style={{ objectPosition }}
              fill={true}
              sizes="100vw"
              src={src}
            />
          );
        })()}
      </div>
      <div className="overlay bg-black/20 pointer-events-none col-start-1 col-span-1 row-start-1 row-span-1 relative" />
      <div className={`self-center col-start-1 col-span-1 row-start-1 row-span-1 rte content ${alignment} grid grid-cols-4 md:grid-cols-12 gap-4 marketing-container px-5 md:px-7.5 py-16 relative`}>
        <div className={`rte text-beige ${alignment == 'left' ? 'col-start-1 col-span-2 md:col-span-6' : 'col-start-2 col-span-2 md:col-start-4 md:col-span-6 text-center'}`}>
          <CustomPortableText value={content as PortableTextBlock[]} />
        </div>
      </div>
    </section>
  )
}
